import { Injectable, inject } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { environment } from '../../../environments/environment';

export interface TeamUser {
  id: string;
  email: string;
  fullName?: string;
  position?: string;
  birthday?: string;
  systemRole: SystemRole;
  roleName: string;
  status: UserStatus;
  mustChangePassword: boolean;
  createdAt: string;
  updatedAt: string;
  photoUrl?: string;
  pluginAccess: PluginAccess[];
}

export interface TeamUserList {
  id: string;
  email: string;
  fullName?: string;
  position?: string;
  systemRole: SystemRole;
  status: UserStatus;
  createdAt: string;
  photoUrl?: string;
  isProtected: boolean; // Indicates if user is protected from deletion/role changes
}

export interface PluginAccess {
  plugin: PluginType;
  pluginName: string;
  hasAccess: boolean;
}

export interface CreateTeamUser {
  email: string;
  fullName?: string;
  position?: string;
  birthday?: string;
  password?: string;
  systemRole: SystemRole;
  mustChangePassword?: boolean;
  photoUrl?: string;
  pluginAccess: PluginAccess[];
}

export interface UpdateTeamUser {
  fullName?: string;
  position?: string;
  birthday?: string;
  photoUrl?: string;
  systemRole?: SystemRole;
  pluginAccess?: PluginAccess[];
}

export interface RoleOption {
  value: number;
  label: string;
}

export interface PluginOption {
  value: number;
  label: string;
  hasAccess: boolean;
}

export interface SystemRoleDto {
  role: SystemRole;
  name: string;
  description: string;
  isAvailableForAssignment: boolean;
}

export interface PluginAccessDto {
  plugin: PluginType;
  hasAccess: boolean;
}

export enum SystemRole {
  SuperAdmin = 1,
  Admin = 2,
  BIMManager = 3,
  Designer = 4
}

export enum UserStatus {
  Pending = 0,
  Active = 1,
  Inactive = 2,
  Deleted = 3
}

export enum PluginType {
  Anguleris = 1,
  MaterialTools = 2,
  Tuuci = 3
}

@Injectable({
  providedIn: 'root'
})
export class TeamUserService {
  private readonly apiUrl = `${environment.apiUrl}/users`;

  private readonly http = inject(HttpClient);

  getUsers(): Observable<TeamUserList[]> {
    return this.http.get<TeamUserList[]>(this.apiUrl);
  }

  getUser(id: string): Observable<TeamUser> {
    return this.http.get<TeamUser>(`${this.apiUrl}/${id}`);
  }

  createUser(user: CreateTeamUser): Observable<TeamUser> {
    return this.http.post<TeamUser>(this.apiUrl, user);
  }

  updateUser(id: string, user: UpdateTeamUser): Observable<TeamUser> {
    return this.http.put<TeamUser>(`${this.apiUrl}/${id}`, user);
  }

  deleteUser(id: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${id}`);
  }

  activateUser(id: string): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.apiUrl}/${id}/activate`, {});
  }

  deactivateUser(id: string): Observable<{ message: string }> {
    return this.http.patch<{ message: string }>(`${this.apiUrl}/${id}/deactivate`, {});
  }

  getAvailableRoles(): Observable<RoleOption[]> {
    return this.http.get<SystemRoleDto[]>(`${this.apiUrl}/roles`).pipe(
      map(roles => roles
        .filter(role => role.isAvailableForAssignment) // Filter out SuperAdmin
        .map(role => ({
          value: role.role,
          label: role.name
        }))
      )
    );
  }

  getAvailablePlugins(): Observable<PluginOption[]> {
    return this.http.get<PluginAccessDto[]>(`${this.apiUrl}/plugins`).pipe(
      map(plugins => plugins.map(plugin => ({
        value: plugin.plugin,
        label: this.getPluginDisplayName(plugin.plugin),
        hasAccess: plugin.hasAccess
      })))
    );
  }

  // Helper methods
  getRoleDisplayName(role: SystemRole): string {
    switch (role) {
      case SystemRole.SuperAdmin:
        return 'Super Administrator';
      case SystemRole.Admin:
        return 'Administrator';
      case SystemRole.BIMManager:
        return 'BIM Manager';
      case SystemRole.Designer:
        return 'Designer';
      default:
        return 'Unknown';
    }
  }

  getStatusText(status: UserStatus): string {
    switch (status) {
      case UserStatus.Pending:
        return 'Pending';
      case UserStatus.Active:
        return 'Active';
      case UserStatus.Inactive:
        return 'Inactive';
      case UserStatus.Deleted:
        return 'Deleted';
      default:
        return 'Unknown';
    }
  }

  getStatusBadgeClass(status: UserStatus): string {
    switch (status) {
      case UserStatus.Active:
        return 'badge-success';
      case UserStatus.Pending:
        return 'badge-warning';
      case UserStatus.Inactive:
        return 'badge-danger';
      case UserStatus.Deleted:
        return 'badge-danger';
      default:
        return 'badge-secondary';
    }
  }

  private getPluginDisplayName(plugin: PluginType): string {
    switch (plugin) {
      case PluginType.Anguleris:
        return 'Anguleris';
      case PluginType.MaterialTools:
        return 'Material Tools';
      case PluginType.Tuuci:
        return 'Tuuci';
      default:
        return 'Unknown';
    }
  }

  getDefaultPluginAccess(): PluginAccess[] {
    return [
      { plugin: PluginType.Anguleris, pluginName: 'Anguleris', hasAccess: true },
      { plugin: PluginType.MaterialTools, pluginName: 'MaterialTools', hasAccess: true },
      { plugin: PluginType.Tuuci, pluginName: 'Tuuci', hasAccess: false }
    ];
  }

  // New method for email validation with backend support
  validateEmailForInvitation(email: string): Observable<EmailValidationResult> {
    return this.http.get<EmailValidationResult>(`${this.apiUrl}/validate-email?email=${encodeURIComponent(email)}`);
  }

  // Utility methods for validation
  isValidEmail(email: string): boolean {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }
}

export interface EmailValidationResult {
  isValid: boolean;
  canInvite: boolean;
  status?: string;
  message: string;
  userId?: string;
  createdAt?: string;
  expiresAt?: string;
}